/**
 * PostHog Custom Event Tracking Utilities
 * Tracks custom checkout events with specific business properties
 */

export const NEWSLETTER_SOURCES = {
  SIGNUP_MODAL: 'signup_modal',
  ONE_TAP_MODAL: 'one_tap_modal',
  CHECKOUT: 'checkout',
  FOOTER: 'footer',
};

/**
 * Safely calls PostHog capture if available
 * @param {string} eventName - The name of the event
 * @param {object} properties - Event properties
 */
const capturePostHogEvent = (eventName, properties) => {
  if (!window.posthog || typeof window.posthog.capture !== 'function') {
    console.warn(`[PostHog] Not available, skipped event: ${eventName}`);
    return;
  }

  try {
    window.posthog.capture(eventName, properties);
  } catch (error) {
    console.warn(`[PostHog] Capture failed: ${eventName}`, error);
  }
};

/**
 * Normalize a phone secure value rank to a number, or null when absent/invalid
 * @param {string|number} phoneSecureValue - The raw phone secure value
 * @returns {number|null} Normalized value
 */
export const normalizePhoneSecureValue = (phoneSecureValue) => {
  if (
    phoneSecureValue === undefined ||
    phoneSecureValue === null ||
    `${phoneSecureValue}`.trim() === ''
  ) {
    return null;
  }

  const normalizedPhoneSecureValue = Number(phoneSecureValue);

  return Number.isNaN(normalizedPhoneSecureValue)
    ? null
    : normalizedPhoneSecureValue;
};

/**
 * Generate a unique checkout ID
 * @returns {string} Unique checkout ID
 */
export const generateCheckoutId = () => {
  return `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create checkout ID from sessionStorage
 * @returns {string} Checkout ID
 */
export const getCheckoutId = () => {
  let checkoutId = sessionStorage.getItem('posthog_checkout_id');
  if (!checkoutId) {
    checkoutId = generateCheckoutId();
    sessionStorage.setItem('posthog_checkout_id', checkoutId);
  }
  return checkoutId;
};

/**
 * Clear checkout ID from sessionStorage
 */
export const clearCheckoutId = () => {
  sessionStorage.removeItem('posthog_checkout_id');
};

/**
 * Build common checkout properties
 * @param {object} cart - Cart object
 * @param {object} listing - Listing object
 * @param {string} checkoutId - Checkout ID
 * @returns {object} Common properties
 */
const buildCheckoutProperties = (cart, listing, checkoutId) => {
  const properties = {
    checkout_id: checkoutId,
    purchase_type: cart?.type || '',
    amount: Number(cart?.priceTotal) || 0,
    activity_name: listing?.title || '',
    activity_country: listing?.country || '',
    activity_region: listing?.region || '',
    activity_city: listing?.city || '',
  };

  if (listing?.categories && listing.categories.length > 0) {
    properties.macro_category = listing.categories[0]?.title || '';
    properties.micro_category =
      listing.categories[1]?.title || listing.categories[0]?.title || '';
  }

  return properties;
};

/**
 * Track Checkout Started event
 * @param {object} cart - Cart object
 * @param {object} listing - Listing object
 */
export const trackCheckoutStarted = (cart, listing) => {
  if (!cart || !listing) {
    // console.warn('[PostHog] Missing cart or listing data for Checkout Started');
    return;
  }

  const checkoutId = getCheckoutId();
  const properties = buildCheckoutProperties(cart, listing, checkoutId);

  capturePostHogEvent('Checkout Started', properties);
};

/**
 * Track Checkout Completed event
 * @param {string} orderId - WooCommerce order ID
 * @param {object} cart - Cart object
 * @param {object} listing - Listing object
 */
export const trackCheckoutCompleted = (orderId, cart, listing) => {
  if (!orderId || !cart || !listing) {
    // console.warn('[PostHog] Missing required data for Checkout Completed');
    return;
  }

  const checkoutId = sessionStorage.getItem('posthog_checkout_id') || '';
  const properties = {
    ...buildCheckoutProperties(cart, listing, checkoutId),
    order_id: orderId.toString(),
  };

  capturePostHogEvent('Checkout Completed', properties);

  // Clear checkout ID after successful completion
  clearCheckoutId();
};

/**
 * Track Dedication Page Viewed event (for vouchers only)
 * @param {object} cart - Cart object
 * @param {object} listing - Listing object
 */
export const trackDedicationPageViewed = (cart, listing) => {
  // Only track for voucher and giftcard purchases
  if (!cart || (cart.type !== 'voucher' && cart.type !== 'giftcard')) {
    return;
  }

  if (!listing) {
    // console.warn('[PostHog] Missing listing data for Dedication Page Viewed');
    return;
  }

  const checkoutId = getCheckoutId();
  const properties = buildCheckoutProperties(cart, listing, checkoutId);

  capturePostHogEvent('Dedication Page Viewed', properties);
};

/**
 * Track clicks on listing phone buttons that have a phone secure value rank.
 * @param {string|number} phoneSecureValue - The listing phone secure value rank
 */
export const trackRankedPhoneButtonClicked = (phoneSecureValue) => {
  const normalizedPhoneSecureValue =
    normalizePhoneSecureValue(phoneSecureValue);

  if (normalizedPhoneSecureValue === null) {
    return;
  }

  capturePostHogEvent('Listing Phone Button Clicked', {
    phone_secure_value: normalizedPhoneSecureValue,
  });
};

/**
 * Track a change of a newsletter checkbox.
 * @param {boolean} isOptedIn - The state of the checkbox after the change
 * @param {string} source - A value of NEWSLETTER_SOURCES
 * @param {object} [extraProperties] - More properties to add to the event
 */
export const trackNewsletterToggle = (isOptedIn, source, extraProperties) => {
  capturePostHogEvent('Newsletter Toggled', {
    newsletter_opt_in: !!isOptedIn,
    source: source,
    ...extraProperties,
  });
};

/**
 * Track the newsletter choice that a completed flow kept.
 * @param {boolean} isOptedIn - The choice that the flow kept
 * @param {string} source - A value of NEWSLETTER_SOURCES
 * @param {object} [extraProperties] - More properties to add to the event
 */
export const trackNewsletterConsentRecorded = (
  isOptedIn,
  source,
  extraProperties
) => {
  capturePostHogEvent('Newsletter Consent Recorded', {
    newsletter_opt_in: !!isOptedIn,
    source: source,
    ...extraProperties,
  });
};

/**
 * Track search pageview with search query as super property
 * The property is page-scoped: it stays registered for the page's lifetime
 * so the initial $pageview (deferred by the async SDK load, hidden tabs and
 * consent banners) never misses it. It is unregistered on pagehide and
 * restored when the page is revived from the back/forward cache; base.twig
 * sweeps leftovers on pages that don't register it themselves.
 * @param {string} searchQuery - The search query to track
 */
export const trackSearchPageview = (searchQuery) => {
  if (!window.posthog || typeof window.posthog.register !== 'function') {
    return;
  }

  const props = { search_query: searchQuery };

  // Flag checked by the sweep in base.twig's `loaded` callback so it
  // doesn't unregister a freshly registered value on this page.
  window.__posthogSearchProps = Object.keys(props);
  window.posthog.register(props);

  window.addEventListener('pagehide', () => {
    window.__posthogSearchProps.forEach((key) => {
      window.posthog.unregister(key);
    });
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.posthog.register(props);
    }
  });
};

/**
 * Helper to ensure PostHog tracking is initialized before tracking
 * Uses event-driven approach with 'posthogloaded' custom event
 * @param {function} trackingFunction - The tracking function to call
 */
export const ensurePostHogLoaded = (trackingFunction) => {
  if (window.posthog) {
    trackingFunction();
  } else {
    window.addEventListener('posthogloaded', trackingFunction);
  }
};
